<template>
    <div
        v-if="documentConfig"
        class="flex flex-col gap-1"
    >
        <DsCard
            bgColor="bg-white"
            :error="documentLevelError"
        >
            <div class="flex flex-col gap-6">
                <DocumentUploadItemHeader
                    :allowDocumentSelection
                    :documentConfig
                    :variant="headerVariant"
                />

                <div
                    v-if="documentConfig.isSelected"
                    class="flex flex-col gap-6"
                    :class="{ 'pl-8': allowDocumentSelection }"
                >
                    <DocumentUploadRecipients
                        v-model:selectedRecipients="selectedRecipients"
                        :clientOptions
                        :documentConfig
                        :documentId
                        @addClient="$emit('addClient')"
                    />

                    <DocumentUploadSigningSetup
                        v-model:needsEsignature="needsEsignature"
                        v-model:selectedSigners="selectedSigners"
                        :branchId
                        :clientOptions="selectedRecipients"
                        :documentId
                        :routes
                    />
                </div>
            </div>
        </DsCard>
    </div>
</template>

<script setup lang="ts">
import type { DocumentUploadItemHeaderProps } from '@/components/Views/Company/File/Documents/DocumentUploadItemHeader.vue';
import DocumentUploadItemHeader from '@/components/Views/Company/File/Documents/DocumentUploadItemHeader.vue';
import DocumentUploadRecipients from '@/components/Views/Company/File/Documents/DocumentUploadRecipients.vue';
import DocumentUploadSigningSetup from '@/components/Views/Company/File/Documents/DocumentUploadSigningSetup.vue';
import type { ClientOptionInput } from '@/components/Views/Company/File/Documents/document-upload-utils';
import { getClientOptions } from '@/components/Views/Company/File/Documents/document-upload-utils';
import type { ClientOption, DocumentActionRoutes, DocumentUploadConfig } from '@/components/Views/Company/File/file.types';
import { DocumentUploadMergedErrorKeys } from '@/components/Views/Company/File/file.types';
import { useFileDocumentStore } from '@/stores/file-document.store';
import type { Branch } from '@/types/closinglock.types';
import DsCard from '@ds/DsCard.vue';
import { computed, nextTick, ref, watch } from 'vue';

const { DOCUMENT_LEVEL_ERROR } = DocumentUploadMergedErrorKeys;

const props = withDefaults(
    defineProps<{
        clients: ClientOptionInput[];
        branchId: Branch['id'];
        documentId: DocumentUploadConfig['documentId'];
        routes: DocumentActionRoutes;
        allowDocumentSelection: boolean;
        headerVariant: DocumentUploadItemHeaderProps['variant'];
    }>(),
    {
        allowDocumentSelection: false,
    },
);

defineEmits<{
    (e: 'addClient'): void;
}>();

const fileDocumentStore = useFileDocumentStore();

const documentConfig = computed(() => fileDocumentStore.documentConfigs.get(props.documentId)!);

// Client selection and signer setup
const clientOptions = computed(() => getClientOptions(props.clients));

const selectedRecipients = ref<ClientOption[]>([]);
const selectedSigners = ref<ClientOption[]>([]);
const needsEsignature = ref(documentConfig.value.needsEsignature);

const documentErrors = computed(() => documentConfig?.value?.mergedErrors ?? new Map());
const documentLevelError = computed(() =>
    [documentErrors.value.get(DOCUMENT_LEVEL_ERROR), documentConfig.value.esignStateInvalidationError].filter(Boolean).join(' '),
);

// Keep the selected recipients — and therefore the signers list, which is derived from
// them — in sync with the underlying client list. On first run, preselect the document's
// existing action clients; on later changes, drop recipients that no longer exist and
// refresh the rest to the latest option data.
watch(
    clientOptions,
    (options, previousOptions) => {
        if (!previousOptions) {
            nextTick(() => {
                selectedRecipients.value = options.filter(option =>
                    fileDocumentStore.documentActionClients.includes(option.value),
                );
            });
            return;
        }
        const selectedValues = new Set(selectedRecipients.value.map(recipient => recipient.value));
        selectedRecipients.value = options.filter(option => selectedValues.has(option.value));
    },
    { immediate: true, flush: 'post' },
);

// Applies changes to the recipients (name, email, etc) to the signer collection, keeping the same items selected
watch(
    selectedRecipients,
    newRecipients => {
        const selectedSignerValues = new Set(selectedSigners.value.map(signer => signer.value));
        selectedSigners.value = newRecipients.filter(recipient => selectedSignerValues.has(recipient.value));
    },
    { immediate: true, deep: true },
);
</script>
